import { useState } from "react";

import { ghostBtnClass } from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import type {
  InboundAction,
  InboundCustomerThread,
  InboundMappedLocation,
} from "@/lib/orderIntakeCustomersClient";
import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import type { OrgLocation } from "@/lib/orgLocations/orgLocationsTypes";
import {
  confirmedThreadLocation,
  correlationImportLabel,
  correlationMatchLabel,
  formatInboundDate,
  hiddenLowValueActionCount,
  inboundAssetLabel,
  inboundAssetStatusLabel,
  ideaRoomActivityLabel,
  threadLocationHints,
  visibleInboundActions,
} from "./ideaRoomCustomerInboxModel";

function ActionList({ actions }: { actions: InboundAction[] }) {
  const [showLowValue, setShowLowValue] = useState(false);
  const hidden = hiddenLowValueActionCount(actions);
  const visible = visibleInboundActions(actions, showLowValue);
  return (
    <div className="space-y-2">
      {hidden ? (
        <button
          type="button"
          className="text-xs font-medium text-indigo-700 hover:underline dark:text-indigo-300"
          onClick={() => setShowLowValue((value) => !value)}
        >
          {showLowValue
            ? "Collapse visited activity"
            : `Show ${hidden} visited action${hidden === 1 ? "" : "s"}`}
        </button>
      ) : null}
      {visible.map((action) => (
        <details
          key={action.id}
          className="rounded-lg border border-gray-200 px-3 py-2 dark:border-zinc-700"
        >
          <summary className="cursor-pointer text-xs">
            <strong>{ideaRoomActivityLabel(action)}</strong>
            <span className="ml-2 text-gray-500 dark:text-zinc-400">
              {formatInboundDate(action.created_at)}
            </span>
            {action.processing_status === "error" ? (
              <span className="ml-2 text-red-600 dark:text-red-300">Error</span>
            ) : null}
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all text-[11px] text-gray-600 dark:text-zinc-300">
            {JSON.stringify(action.payload ?? {}, null, 2)}
          </pre>
        </details>
      ))}
    </div>
  );
}

function locationName(
  locationId: string | null | undefined,
  activeLocations: OrgLocation[],
  mappedLocations: InboundMappedLocation[]
): string {
  if (!locationId) return "None";
  return (
    activeLocations.find((location) => location.id === locationId)?.name ??
    mappedLocations.find((location) => location.id === locationId)?.name ??
    locationId
  );
}

export function IdeaRoomCustomerThreadCard({
  thread,
  activeLocations,
  mappedLocations,
  confirming,
  onConfirm,
  onOpenIntake,
  assetActionId,
  onAssetAction,
}: {
  thread: InboundCustomerThread;
  activeLocations: OrgLocation[];
  mappedLocations: InboundMappedLocation[];
  confirming: boolean;
  onConfirm: (threadId: string, locationId: string) => Promise<boolean>;
  onOpenIntake: (intake: OpsOrderIntakeRow) => void;
  assetActionId: string | null;
  onAssetAction: (candidateId: string, action: "save" | "retry" | "open") => Promise<boolean>;
}) {
  const confirmed = confirmedThreadLocation(thread);
  const [locationId, setLocationId] = useState(
    confirmed?.location_id ?? thread.proposed_location_id ?? ""
  );
  const hints = threadLocationHints(thread);
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-zinc-700 dark:bg-zinc-900/50">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
            {thread.actions[0]
              ? ideaRoomActivityLabel(thread.actions[0])
              : thread.latest_event_type || "IdeaRoom order"}
          </div>
          <div className="text-xs text-gray-500 dark:text-zinc-400">
            {thread.stable_key} · {formatInboundDate(thread.latest_activity_at)}
          </div>
        </div>
        {thread.intake ? (
          <button type="button" className={ghostBtnClass} onClick={() => onOpenIntake(thread.intake!)}>
            Review {thread.intake.status.replaceAll("_", " ")}
          </button>
        ) : (
          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-950 dark:text-blue-200">
            Supporting design activity
          </span>
        )}
      </div>

      {thread.correlations.map((correlation) => (
        <div
          key={correlation.id}
          className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs dark:border-indigo-900 dark:bg-indigo-950/30"
        >
          <div className="font-semibold">{correlationMatchLabel(correlation)}</div>
          <div className="mt-1 break-all text-gray-700 dark:text-zinc-300">
            IdeaRoom hash: {correlation.idearoom_event_hash}
          </div>
          <div className="text-gray-600 dark:text-zinc-400">
            ShedSuite quote: {correlation.shedsuite_quote_id ?? "Not matched"} · order:{" "}
            {correlation.shedsuite_order_number ?? correlation.customer_order_id ?? "Not matched"}
          </div>
          <div
            className={correlation.import_status === "soft_failed" ? "text-red-700 dark:text-red-300" : "text-gray-600 dark:text-zinc-400"}
            title={correlation.import_error ?? undefined}
          >
            {correlationImportLabel(correlation)}
          </div>
          {correlation.exact_floor_plan ? (
            <div className="break-all text-gray-500 dark:text-zinc-500">
              Exact floor plan: {correlation.exact_floor_plan.source_field} · event{" "}
              {correlation.exact_floor_plan.event_id}
            </div>
          ) : null}
        </div>
      ))}

      {thread.identity_conflict ? (
        <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          Identity conflict: email and phone resolve to different customer profiles. Do not merge
          automatically.
        </div>
      ) : null}

      <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-950">
        <div className="text-xs font-semibold text-gray-800 dark:text-zinc-200">Location match</div>
        <div className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
          Confirmed: {locationName(confirmed?.location_id, activeLocations, mappedLocations)}
        </div>
        {hints.length ? (
          <div className="mt-1 break-words text-[11px] text-gray-500 dark:text-zinc-500">
            Incoming hints: {hints.join(" · ")}
          </div>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-2">
          <select
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            className="min-w-48 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs dark:border-zinc-600 dark:bg-zinc-900"
          >
            <option value="">Select active location…</option>
            {activeLocations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
                {location.city ? ` — ${location.city}` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={!locationId || confirming}
            onClick={() => void onConfirm(thread.id, locationId)}
          >
            {confirming ? "Confirming…" : confirmed ? "Change match" : "Confirm match"}
          </button>
        </div>
      </div>

      {thread.asset_url_candidates.length ? (
        <div className="mt-3 space-y-1 rounded-lg border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-950">
          {thread.asset_url_candidates.map((asset) => (
            <div key={asset.id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="min-w-24 font-medium">{inboundAssetLabel(asset)}</span>
              <span className="text-gray-500 dark:text-zinc-400">{inboundAssetStatusLabel(asset)}</span>
              <a href={asset.url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline dark:text-indigo-300">
                Open original
              </a>
              {asset.fetch_status === "available" && asset.asset_kind !== "configurator" ? (
                <button
                  type="button"
                  className="text-indigo-700 hover:underline disabled:opacity-50 dark:text-indigo-300"
                  disabled={!thread.intake?.materialized_deal_id || assetActionId === asset.id}
                  onClick={() => void onAssetAction(asset.id, "save")}
                >
                  Save to deal
                </button>
              ) : null}
              {asset.fetch_status === "error" ? (
                <button
                  type="button"
                  className="text-red-700 hover:underline disabled:opacity-50 dark:text-red-300"
                  disabled={!thread.intake?.materialized_deal_id || assetActionId === asset.id}
                  onClick={() => void onAssetAction(asset.id, "retry")}
                  title={asset.fetch_error ?? undefined}
                >
                  Retry
                </button>
              ) : null}
              {asset.fetch_status === "imported" ? (
                <button
                  type="button"
                  className="text-emerald-700 hover:underline dark:text-emerald-300"
                  disabled={assetActionId === asset.id}
                  onClick={() => void onAssetAction(asset.id, "open")}
                >
                  Open saved attachment
                </button>
              ) : null}
            </div>
          ))}
          {!thread.intake?.materialized_deal_id ? (
            <div className="text-[11px] text-amber-700 dark:text-amber-300">
              Accept this intake before saving assets to a deal.
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3">
        <ActionList actions={thread.actions} />
      </div>
    </div>
  );
}
